import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import { Megaphone, Target, Mic2, CalendarDays, Route, ArrowRight, Check, Users, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useLang, STR } from "@/i18n/translations";
import api from "@/lib/api";

const ICONS = { digital: Megaphone, ads: Target, booking: Mic2, events: CalendarDays, tours: Route };

const PLANS = [
  {
    key: "basic", name: "Basic", price: "500€", period: "/mois",
    platforms: "Meta Ads (Facebook + Instagram)",
    reach: { fr: "30 000 – 60 000 personnes touchées / mois", en: "30,000 – 60,000 people reached / month" },
    features: {
      fr: ["Campagne Meta Ads (Facebook & Instagram)", "Ciblage d'audience personnalisé", "Création de visuels publicitaires", "Rapport de performance mensuel"],
      en: ["Meta Ads campaign (Facebook & Instagram)", "Custom audience targeting", "Ad creative design", "Monthly performance report"],
    },
  },
  {
    key: "standard", name: "Standard", price: "700€", period: "/mois",
    platforms: "Meta + TikTok",
    reach: { fr: "60 000 – 120 000 personnes touchées / mois", en: "60,000 – 120,000 people reached / month" },
    features: {
      fr: ["Tout le Basic", "Campagnes TikTok Ads incluses", "Optimisation multi-plateformes", "Suivi hebdomadaire"],
      en: ["Everything in Basic", "TikTok Ads included", "Multi-platform optimization", "Weekly tracking"],
    },
  },
  {
    key: "standard_boost", name: "Standard Boost", price: "1000€", period: "/mois",
    platforms: "Meta + TikTok",
    highlight: true,
    reach: { fr: "120 000 – 240 000 personnes — 2× plus d'audience", en: "120,000 – 240,000 people — 2× more audience" },
    features: {
      fr: ["Tout le Standard", "Budget média renforcé : 2× plus de personnes touchées", "Retargeting avancé", "A/B testing des créations"],
      en: ["Everything in Standard", "Boosted media budget: 2× more people reached", "Advanced retargeting", "A/B creative testing"],
    },
  },
  {
    key: "premium", name: "Premium", price: "1500€", period: "/mois",
    platforms: "Facebook + TikTok + Google Ads",
    reach: { fr: "250 000+ personnes / mois — couverture multi-plateformes", en: "250,000+ people / month — multi-platform coverage" },
    features: {
      fr: ["Tout le Standard Boost", "Google Ads (Search & YouTube)", "Stratégie omnicanale complète", "Accompagnement dédié prioritaire"],
      en: ["Everything in Standard Boost", "Google Ads (Search & YouTube)", "Full omnichannel strategy", "Dedicated priority support"],
    },
  },
];

export default function Services() {
  const { t, lang } = useLang();
  const formRef = useRef(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", plan: "", budget: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const serviceOptions = STR.services.items.map((s) => (lang === "fr" ? s.fr : s.en));

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const selectService = (name) => {
    setForm((f) => ({ ...f, service: name, plan: "" }));
    setSent(false);
    setTimeout(scrollToForm, 50);
  };

  const selectPlan = (plan) => {
    setForm((f) => ({ ...f, service: t({ fr: "Publicité en ligne (campagne)", en: "Online advertising (campaign)" }), plan }));
    setSent(false);
    setTimeout(scrollToForm, 50);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/service-requests", form);
      setSent(true);
      toast.success(t({ fr: "Demande envoyée ! Nous vous recontactons vite.", en: "Request sent! We'll get back to you soon." }));
      setForm({ name: "", email: "", phone: "", service: "", plan: "", budget: "", message: "" });
    } catch {
      toast.error(t({ fr: "Erreur, réessayez.", en: "Error, please retry." }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pt-32 pb-24">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <p className="eyebrow mb-5">{t(STR.services.eyebrow)}</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold max-w-3xl">{t(STR.services.title)}</h1>
        <div className="gold-line w-24 my-10" />

        {/* Services list */}
        <div className="space-y-6">
          {STR.services.items.map((s, i) => {
            const Icon = ICONS[s.key];
            const name = lang === "fr" ? s.fr : s.en;
            return (
              <motion.div
                key={s.key}
                initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="card-dark rounded-2xl p-8 lg:p-10 grid lg:grid-cols-[auto_1fr_auto] gap-8 items-center"
                data-testid={`service-${s.key}`}
              >
                <div className="flex items-center gap-6">
                  <span className="font-serif text-5xl text-gold/30">0{i + 1}</span>
                  <div className="w-14 h-14 rounded-xl bg-gold/10 border border-gold/30 flex items-center justify-center">
                    <Icon className="w-6 h-6 text-gold" />
                  </div>
                </div>
                <div>
                  <h2 className="font-serif text-2xl lg:text-3xl mb-2">{name}</h2>
                  <p className="text-cream/60 leading-relaxed">{lang === "fr" ? s.dfr : s.den}</p>
                </div>
                {s.key === "ads" ? (
                  <a href="#tarifs-pub" className="btn-outline-gold rounded-full px-6 py-3 text-sm whitespace-nowrap flex items-center gap-2 justify-center">
                    {t({ fr: "Voir les tarifs", en: "See pricing" })} <ArrowRight className="w-4 h-4" />
                  </a>
                ) : (
                  <button onClick={() => selectService(name)} data-testid={`service-request-${s.key}`} className="btn-outline-gold rounded-full px-6 py-3 text-sm whitespace-nowrap flex items-center gap-2 justify-center">
                    {t({ fr: "Demander ce service", en: "Request this service" })} <ArrowRight className="w-4 h-4" />
                  </button>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Advertising pricing */}
        <div id="tarifs-pub" className="mt-24 scroll-mt-28">
          <p className="eyebrow mb-4">{t({ fr: "Publicité en ligne", en: "Online advertising" })}</p>
          <h2 className="font-serif text-3xl lg:text-5xl font-bold max-w-3xl">
            {t({ fr: "Des campagnes qui touchent votre audience", en: "Campaigns that reach your audience" })}
          </h2>
          <p className="text-cream/60 mt-4 max-w-2xl">
            {t({ fr: "Choisissez la formule adaptée à vos objectifs. Audiences estimées à titre indicatif selon le ciblage et le budget média.", en: "Choose the plan that fits your goals. Estimated audiences are indicative, based on targeting and media budget." })}
          </p>

          <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6 mt-10">
            {PLANS.map((p, i) => (
              <motion.div
                key={p.key}
                initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                data-testid={`plan-${p.key}`}
                className={`relative rounded-2xl p-7 flex flex-col border ${p.highlight ? "border-gold bg-gold/[0.06]" : "border-gold/15 card-dark"}`}
              >
                {p.highlight && (
                  <span className="absolute -top-3 left-7 bg-gold text-obsidian text-xs font-semibold px-3 py-1 rounded-full">
                    {t({ fr: "Le plus populaire", en: "Most popular" })}
                  </span>
                )}
                <h3 className="font-serif text-2xl">{p.name}</h3>
                <p className="text-xs text-cream/50 mt-1">{p.platforms}</p>
                <div className="mt-4 flex items-end gap-1">
                  <span className="font-serif text-4xl text-gold">{p.price}</span>
                  <span className="text-cream/50 text-sm mb-1">{t({ fr: "/mois", en: "/month" })}</span>
                </div>

                <div className="mt-5 rounded-xl bg-gold/10 border border-gold/25 p-4">
                  <div className="flex items-center gap-2 text-gold text-xs uppercase tracking-widest mb-1">
                    <Users className="w-4 h-4" /> {t({ fr: "Audience estimée", en: "Estimated reach" })}
                  </div>
                  <p className="text-sm text-cream font-medium">{t(p.reach)}</p>
                </div>

                <ul className="mt-5 space-y-2.5 flex-1">
                  {(lang === "fr" ? p.features.fr : p.features.en).map((f, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-cream/75">
                      <Check className="w-4 h-4 text-gold mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>

                <button onClick={() => selectPlan(`${p.name} — ${p.price}${t({ fr: "/mois", en: "/month" })} (${p.platforms})`)} data-testid={`plan-select-${p.key}`}
                  className={`mt-6 rounded-full py-3 text-sm font-medium w-full ${p.highlight ? "btn-gold" : "btn-outline-gold"}`}>
                  {t({ fr: "Choisir cette formule", en: "Choose this plan" })}
                </button>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Service inquiry form */}
        <div ref={formRef} id="devis" className="mt-24 scroll-mt-28 card-dark rounded-2xl p-8 lg:p-12 max-w-3xl mx-auto">
          <p className="eyebrow mb-4">{t({ fr: "Prendre contact", en: "Get in touch" })}</p>
          <h2 className="font-serif text-3xl lg:text-4xl mb-3">
            {t({ fr: "Parlez-nous de votre projet", en: "Tell us about your project" })}
          </h2>
          <p className="text-cream/60 mb-8">
            {t({ fr: "Publicité digitale, organisation de tournée, événement… Laissez-nous vos coordonnées, nous revenons vers vous rapidement.", en: "Digital advertising, tour organization, events… Leave your details and we'll get back to you quickly." })}
          </p>

          {sent ? (
            <div className="text-center py-10" data-testid="service-success">
              <Check className="w-12 h-12 text-gold mx-auto mb-4" />
              <p className="text-cream text-lg">{t({ fr: "Merci ! Votre demande a bien été envoyée.", en: "Thank you! Your request has been sent." })}</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="service-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label={t({ fr: "Nom complet", en: "Full name" })} required>
                  <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="service-name-input"
                    className="inp" />
                </Field>
                <Field label={t({ fr: "Service souhaité", en: "Desired service" })}>
                  <select value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} data-testid="service-select" className="inp">
                    <option value="">{t({ fr: "Sélectionner…", en: "Select…" })}</option>
                    {serviceOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                    <option value={t({ fr: "Publicité en ligne (campagne)", en: "Online advertising (campaign)" })}>
                      {t({ fr: "Publicité en ligne (campagne)", en: "Online advertising (campaign)" })}
                    </option>
                  </select>
                </Field>
                <Field label="Email" required>
                  <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="service-email-input"
                    className="inp" placeholder={t({ fr: "vous@email.com", en: "you@email.com" })} />
                </Field>
                <Field label={t({ fr: "Téléphone", en: "Phone" })} required>
                  <input type="tel" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="service-phone-input"
                    className="inp" placeholder="+33 6 12 34 56 78" />
                </Field>
              </div>

              {form.plan && (
                <div className="rounded-lg bg-gold/10 border border-gold/25 px-4 py-3 text-sm" data-testid="service-selected-plan">
                  <span className="text-cream/60">{t({ fr: "Formule sélectionnée :", en: "Selected plan:" })} </span>
                  <span className="text-gold">{form.plan}</span>
                </div>
              )}

              <Field label={t({ fr: "Budget (optionnel)", en: "Budget (optional)" })}>
                <input value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} data-testid="service-budget-input" className="inp" />
              </Field>
              <Field label={t({ fr: "Votre message", en: "Your message" })}>
                <textarea rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="service-message-input"
                  className="inp resize-none" />
              </Field>

              <p className="text-xs text-cream/40">
                {t({ fr: "* Email et téléphone obligatoires pour vous recontacter.", en: "* Email and phone required so we can reach you." })}
              </p>
              <button type="submit" disabled={busy} data-testid="service-submit-btn" className="btn-gold rounded-full w-full py-4 flex items-center justify-center gap-2 disabled:opacity-60">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                {t({ fr: "Envoyer ma demande", en: "Send my request" })}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-cream/50 mb-1.5 block">
        {label}{required && <span className="text-gold"> *</span>}
      </label>
      {children}
    </div>
  );
}
